import React, { useCallback, useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { HsmContactBatch } from '../../types';

export interface BatchEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: HsmContactBatch | null;
  onSave: (batchId: number, name: string, description?: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
}

export function BatchEditDialog({
  open,
  onOpenChange,
  batch,
  onSave
}: BatchEditDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when batch changes
  useEffect(() => {
    if (batch) {
      setName(batch.name);
      setDescription(batch.description || '');
    } else {
      setName('');
      setDescription('');
    }
    setError(null);
  }, [batch]);

  const handleSave = useCallback(async () => {
    if (!batch || !name.trim()) return;

    setSaving(true);
    setError(null);

    try {
      const response = await onSave(batch.id, name.trim(), description.trim() || undefined);
      if (response.success) {
        onOpenChange(false);
      } else {
        setError(response.error || 'Erro ao salvar lote');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar lote');
    } finally {
      setSaving(false);
    }
  }, [batch, name, description, onSave, onOpenChange]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!saving) {
      onOpenChange(newOpen);
    }
  }, [saving, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Editar Lote
          </DialogTitle>
          <DialogDescription>
            Altere o nome ou descricao do lote.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-batch-name">Nome do Lote</Label>
            <Input
              id="edit-batch-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do lote"
              disabled={saving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-batch-description">Descricao (opcional)</Label>
            <Textarea
              id="edit-batch-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descricao do lote..."
              rows={3}
              disabled={saving}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
